<script setup>
/**
 * FormControlFranchiseId
 * Logic-heavy control for franchise assignment.
 * Locked for franchise users, selectable for global admins.
 */
import {ref, computed, onMounted} from 'vue'
import {useUserStore} from '@/stores/user'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import {useAppStore} from '@/stores/appStore'
import FormControlTypeRelationshipOneToMany
    from '@/common/form-control-type/FormControlTypeRelationshipOneToMany.vue'
import FormControlPlainText from "./FormControlPlainText.vue"

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: null
    }
})

const userStore = useUserStore()
const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)
const appStore = useAppStore()

const franchiseTitle = ref('')

const franchiseId = computed(() => userStore.user?.franchiseId)

/**
 * Support for multi-site table naming conventions.
 */
const franchiseTableName = computed(() => {
    return appStore.sitename?.startsWith('outdoorsproject')
        ? 'odp_franchises'
        : 'franchise'
})

/**
 * Resolves the display name of the franchise if the user is locked to one.
 */
const fetchFranchiseTitle = async () => {
    if (!franchiseId.value) return

    try {
        const obj = await itemDataStore.get({
            tableName: franchiseTableName.value,
            id: franchiseId.value
        })
        franchiseTitle.value = obj?.title || ''
    } catch (error) {
        console.error("Failed to fetch franchise title:", error)
    }
}

onMounted(() => {
    if (franchiseId.value) {
        // Force the form data to match the user's franchise restriction
        formStore.presetData({
            name: 'franchiseId',
            value: franchiseId.value
        })

        fetchFranchiseTitle()
    }
})
</script>

<template>
    <FormControlPlainText
        v-if="franchiseId"
        :value="franchiseTitle"
        :form-id="formId"
    />

    <div v-else>
        <FormControlTypeRelationshipOneToMany v-bind="$props"/>
    </div>
</template>